// Types
import { ShortTxnResponse } from 'app/modules/transactions/types';
import { ShortActionEventResponse } from 'app/modules/actionEvents/responses';
import { ShortEntityResponse } from 'app/modules/entities/responses';
import { ReactElement } from 'react';

// Models
import {
  AlertsSearchResult,
  CasesSearchResult,
  EntitiesSearchResult,
  EntitiesSearchV2Result,
  EventsSearchResult,
  FlowsSearchResult,
  InstrumentsSearchResult,
  NoteOwnersType,
  NotesSearchResult,
  RulesSearchResult,
  SearchResultProps,
  TeamsSearchResult,
} from 'app/modules/search/models';
import {
  U21Avatar,
  U21SelectOptionProps,
  U21UnnestedSelectOption,
} from 'app/shared/u21-ui/components';
import { ShortInstrumentResponse } from 'app/modules/instruments/responses';

// Utils
import { ROUTES_MAP } from 'app/shared/utils/routes';

// Constants
import { CUSTOM_DATA_SEPARATOR } from 'app/shared/constants';

// Components
import {
  IconBriefcase,
  IconBuilding,
  IconBuildingBank,
  IconCash,
  IconCoin,
  IconCreditCard,
  IconFlag,
  IconGitFork,
  IconLock,
  IconScale,
  IconUser,
  IconUsers,
} from '@u21/tabler-icons';
import {
  ActionEventSource,
  CaseSource,
  AlertSource,
  EntitySource,
  RuleSource,
  InstrumentSource,
  TransactionSource,
} from 'app/modules/search/response';
import { ShortAgentResponse } from 'app/modules/agents/responses';
import { getAgentDisplayName } from 'app/modules/agents/utils';
import { SIDEPANEL_SEARCH_PARAM_KEY } from 'app/shared/components/SidePanel/SidePanelLayout';

export enum ValueField {
  ID = 'ID',
  EXTERNAL_ID = 'EXTERNAL_ID',
}

export const formatAgentOptions = (
  agents: ShortAgentResponse[],
): U21SelectOptionProps<number>[] =>
  agents.map((agent) => {
    const { id, picture } = agent;

    const name = getAgentDisplayName(agent);

    return {
      icon: <U21Avatar name={name} src={picture} />,
      text: name,
      value: id,
    };
  });

export const formatEntityTopBarSearch = (
  entity: EntitiesSearchResult | EntitiesSearchV2Result,
): SearchResultProps => {
  const {
    external_id: externalId,
    first_name: firstName,
    last_name: lastName,
    middle_name: middleName,
    id,
    type,
  } = entity;
  // This is a temporary change to support both V1 and V2 search until V1 search is removed
  let email: string[] = [];
  if ('email' in entity && entity.email) {
    email = entity.email;
  } else if ('email_addresses' in entity && entity.email_addresses) {
    email = entity.email_addresses;
  }
  let businessName = '';
  if ('name' in entity && entity.name) {
    businessName = entity.name;
  } else if ('business_name' in entity && entity.business_name) {
    businessName = entity.business_name;
  }
  const customData = (entity as EntitiesSearchResult).custom_data;

  let title = businessName;
  let description = email.join(', ') || '';

  if (firstName) {
    // added for spacing
    title += ` ${firstName}`;
  }

  if (middleName) {
    // added for spacing
    title += ` ${middleName}`;
  }

  if (lastName) {
    // added for spacing
    title += ` ${lastName}`;
  }

  // if title is still empty, add external id as main title
  if (title.length <= 0) {
    title = externalId;
    // otherwise add external id to the side description
  } else {
    // added for spacing
    description += ` ${externalId}`;
  }

  if (customData && customData.includes('customer_raw')) {
    const customDataObj = JSON.parse(customData);
    let customDataStr = '';
    Object.entries(customDataObj.customer_raw).forEach(([key, value]) => {
      if (typeof value === 'string' || typeof value === 'number') {
        customDataStr += `${key} : ${value}`;
      } else if (typeof value === 'object' && value) {
        // value truthy check is to catch null
        Object.entries(value).forEach(([innerKey, innerValue]) => {
          if (
            typeof innerValue === 'string' ||
            typeof innerValue === 'number'
          ) {
            customDataStr += `${innerKey} : ${innerValue}`;
          }
        });
      }
    });
    if (customDataStr !== '') {
      description += ` ${CUSTOM_DATA_SEPARATOR} ${customDataStr}`;
    }
  }

  // For entities returned from V1 search, this is computed on the BE.
  // For entities returned from V2 search, this is computed by getDisplayName below.
  // Once V1 search is removed, we can remove all of the title computation logic above.
  const nameReadable = entity.name_readable;
  if (nameReadable) {
    title = nameReadable;
  }

  return {
    value: id,
    title,
    description: description.trim(),
    label: type ?? undefined,
    icon: <IconUser />,
    url: ROUTES_MAP.entitiesId.path.replace(':id', String(entity.id)),
  };
};

export const formatEntityOptions = (
  entities: EntitiesSearchResult[] | ShortEntityResponse[] | EntitySource[],
  searchType?: string,
  valueField?: ValueField,
  parseUnit21Id: boolean = true,
  usingPgCache: boolean = false,
): U21UnnestedSelectOption<string | number>[] => {
  return entities.map((i) => {
    const {
      email,
      email_addresses: emailAddresses,
      external_id: entityId,
      first_name: firstName,
      last_name: lastName,
      middle_name: middleName,
      name_readable: nameReadable,
      name,
      business_name: businessName,
      type,
      id,
      unit21_id: unit21Id,
    } = i;

    let text;
    if (searchType === 'email_address') {
      // Prioritize V2 email_addresses field, fallback to V1 email field
      if (emailAddresses?.length) {
        text = emailAddresses.join(', ');
      } else if (email) {
        // Handle V1 email field which could be a string or string[]
        text = Array.isArray(email) ? email.join(', ') : email;
      } else {
        text = '';
      }
    } else if (nameReadable) {
      text = nameReadable;
    } else {
      // Use businessName for V2, fallback to name for V1 compatibility
      text = businessName || name || '';
      if (firstName) {
        text += ` ${firstName}`;
      }
      if (middleName) {
        text += ` ${middleName}`;
      }
      if (lastName) {
        text += ` ${lastName}`;
      }
      text = text.trim();
    }

    let primaryKeyValue: string | number;

    if (usingPgCache) {
      // If using PG cache, trust that id is a dl_hash_id string
      primaryKeyValue = id;
    } else if (parseUnit21Id) {
      if (unit21Id) {
        primaryKeyValue = parseInt(unit21Id, 10);
      } else {
        // If not using PG cache, fall back to id if unit21Id is not present
        primaryKeyValue = parseInt(id, 10);
        if (primaryKeyValue.toString() !== id) {
          // id is alphanumeric, so return as string
          primaryKeyValue = id;
        }
      }
    } else {
      primaryKeyValue = id;
    }

    return {
      icon: type === 'BUSINESS' ? <IconBuilding /> : <IconUser />,
      text: text ? `${entityId} - ${text}` : entityId,
      value: valueField === ValueField.EXTERNAL_ID ? entityId : primaryKeyValue,
    };
  });
};

export const formatCaseOptions = (
  cases: CaseSource[],
): U21SelectOptionProps<number | string>[] =>
  cases.map((currCase) => {
    const { title, id } = currCase;

    return {
      icon: <IconBriefcase />,
      text: `#${id} - ${title}`,
      value: parseInt(id, 10),
    };
  });

export const formatTeamTopBarSearch = (
  team: TeamsSearchResult,
): SearchResultProps => {
  const { id, name, description } = team;
  return {
    value: id,
    title: `#${id} - ${name}`,
    description,
    icon: <IconUsers />,
    url: ROUTES_MAP.teamsId.path.replace(':id', String(id)),
  };
};

export const formatAlertOptions = (
  alerts: AlertSource[],
): U21SelectOptionProps<number | string>[] =>
  alerts.map((alert) => {
    const { title, id } = alert;

    return {
      icon: <IconFlag />,
      text: `#${id} - ${title}`,
      value: parseInt(id, 10),
    };
  });

export const formatAlertTopBarSearch = (
  alert: AlertsSearchResult,
): SearchResultProps => {
  const {
    id,
    title,
    description,
    status,
    queue_id: queueId,
    queue_access_type: queueAccessType,
  } = alert;
  return {
    value: id,
    title: `#${id} - ${title}`,
    description,
    label: status,
    icon: <IconFlag />,
    url: ROUTES_MAP.alertsId.path.replace(':id', String(id)),
    queueId,
    queueAccessType,
  };
};

export const formatCaseTopBarSearch = (
  caseResult: CasesSearchResult,
): SearchResultProps => {
  const {
    id,
    title,
    description,
    status,
    queue_id: queueId,
    queue_access_type: queueAccessType,
  } = caseResult;
  return {
    value: id,
    title: `#${id} - ${title}`,
    description,
    icon: <IconBriefcase />,
    label: status,
    url: ROUTES_MAP.casesId.path.replace(':id', String(id)),
    queueId,
    queueAccessType,
  };
};

export const formatRuleOptions = (
  rules: RuleSource[],
): U21SelectOptionProps<number | string>[] =>
  rules.map((rule) => {
    const { title, id } = rule;

    return {
      icon: <IconScale />,
      text: `#${id} - ${title}`,
      value: parseInt(id, 10),
    };
  });

export const formatRuleTopBarSearch = (
  rule: RulesSearchResult,
): SearchResultProps => {
  const { id, title, description, status } = rule;
  const url =
    status === 'VALIDATION'
      ? ROUTES_MAP.rulesValidationId.path.replace(':id', String(id))
      : ROUTES_MAP.rulesId.path.replace(':id', String(id));
  return {
    value: id,
    title: `#${id} - ${title}`,
    description,
    label: status,
    icon: <IconScale />,
    url,
  };
};

export const formatFlowTopBarSearch = (
  flow: FlowsSearchResult,
): SearchResultProps => {
  const { id, title, description, status } = flow;
  return {
    value: id,
    title: `#${id} - ${title}`,
    description,
    label: status,
    icon: <IconGitFork />,
    url: ROUTES_MAP.flowDetail.path
      .replace(':id', String(id))
      .replace('/:tab?', ''),
  };
};

export const formatTransactionOptions = (
  events: (EventsSearchResult | ShortTxnResponse | TransactionSource)[],
): U21SelectOptionProps<number | string>[] =>
  events.map((event) => ({
    icon: <IconCoin />,
    text: event.external_id,
    value: event.external_id,
  }));

export const formatActionEventOptions = (
  actionEvents: (
    | EventsSearchResult
    | ShortActionEventResponse
    | ActionEventSource
  )[],
): U21SelectOptionProps<number | string>[] =>
  actionEvents.map((event) => {
    const { external_id: externalId } = event;

    return {
      icon: <IconLock />,
      text: externalId,
      value: externalId,
    };
  });

export const formatInstrumentOptions = (
  instruments: (
    | InstrumentsSearchResult
    | ShortInstrumentResponse
    | InstrumentSource
  )[],
): U21SelectOptionProps<number | string>[] => {
  return instruments.map((instrument) => {
    const { external_id: externalId } = instrument;

    return {
      icon: <IconCreditCard />,
      value: externalId,
      text: externalId,
    };
  });
};

export const formatEventTopBarSearch = (
  event: EventsSearchResult,
): SearchResultProps => {
  const { id, external_id: externalId } = event;
  return {
    value: id,
    title: externalId,
    icon: <IconCoin />,
    url: ROUTES_MAP.dataExplorerTransactionsId.path.replace(':id', String(id)),
  };
};

export const formatActionEventsTopBarSearch = (
  event: EventsSearchResult,
): SearchResultProps => {
  const { id, external_id: externalId } = event;
  return {
    value: id,
    title: externalId,
    icon: <IconLock />,
    url: ROUTES_MAP.dataExplorerActionEventsId.path.replace(':id', String(id)),
  };
};

export const formatInstrumentTopBarSearch = (
  instrument: InstrumentsSearchResult,
): SearchResultProps => {
  const { id, external_id: externalId } = instrument;
  return {
    value: id,
    title: externalId,
    icon: <IconCreditCard />,
    url: ROUTES_MAP.instrumentsId.path.replace(':id', String(id)),
  };
};

const NOTE_ICONS: Record<NoteOwnersType, ReactElement> = {
  ALERT: <IconFlag />,
  CASE: <IconBriefcase />,
  SAR_FILING: <IconBuildingBank />,
  CTR_FILING: <IconCash />,
  RULE: <IconScale />,
  INSTRUMENT: <IconCreditCard />,
  ENTITY: <IconUser />,
};

const getNoteRoute = (noteOwnersType: NoteOwnersType, id: string) => {
  switch (noteOwnersType) {
    case 'ALERT':
      return `${ROUTES_MAP.alertsId.path.replace(
        ':id',
        id,
      )}?${SIDEPANEL_SEARCH_PARAM_KEY}=Notes`;
    case 'CASE':
      return `${ROUTES_MAP.casesId.path.replace(
        ':id',
        id,
      )}?${SIDEPANEL_SEARCH_PARAM_KEY}=Notes`;
    case 'SAR_FILING':
      return `${ROUTES_MAP.filingsSarId.path.replace(
        ':id',
        id,
      )}?${SIDEPANEL_SEARCH_PARAM_KEY}=Notes`;
    case 'CTR_FILING':
      return `${ROUTES_MAP.filingsFincenCtrEdit.path.replace(
        ':id/:tab?',
        id,
      )}?${SIDEPANEL_SEARCH_PARAM_KEY}=Notes`;
    case 'RULE':
      return ROUTES_MAP.rulesIdTab.path
        .replace(':id', id)
        .replace(':tab?', 'notes');
    case 'INSTRUMENT':
      return `${ROUTES_MAP.instrumentsId.path.replace(
        ':id',
        id,
      )}?${SIDEPANEL_SEARCH_PARAM_KEY}=Notes`;
    case 'ENTITY':
      return `${ROUTES_MAP.entitiesId.path.replace(
        ':id',
        id,
      )}?${SIDEPANEL_SEARCH_PARAM_KEY}=Notes`;
    default: {
      const exhaustivenessCheck: never = noteOwnersType;
      return exhaustivenessCheck;
    }
  }
};

export const formatNotesTopBarSearch = (
  notes: NotesSearchResult,
): SearchResultProps => {
  const {
    id,
    note_owners_type: noteOwnersType,
    note_owners_title: noteOwnersTitle,
    note_owners_unit21_id: noteOwnersUnit21Id,
    highlight,
  } = notes;
  return {
    value: id,
    title: noteOwnersTitle,
    description: highlight,
    icon: NOTE_ICONS[noteOwnersType],
    url: getNoteRoute(noteOwnersType, noteOwnersUnit21Id),
  };
};

export const stringContainsSubstring = (
  haystack: string,
  needles: string[],
) => {
  return (
    needles.filter((needle) => haystack.includes(needle)).length ===
    needles.length
  );
};

export const mapEntityUnit21IDsToExternalIDs = (
  entityLegacyIds: (string | number)[],
  entitiesSearchResults: EntitiesSearchResult[],
): string[] => {
  const entityUnit21IdToExternalIdMap = entitiesSearchResults.reduce(
    (acc, { id: u21ID, external_id: externalId }) => ({
      ...acc,
      [u21ID]: externalId,
    }),
    {},
  );

  return entityLegacyIds.reduce((externalIds, entityUnit21Id) => {
    const entityExternalId = entityUnit21IdToExternalIdMap[entityUnit21Id];
    if (entityExternalId) {
      return [...externalIds, entityExternalId];
    }
    return externalIds;
  }, []);
};

export function getDisplayName(entity: EntitySource): string {
  if (entity.business_name) {
    return `${entity.business_name}`;
  }
  if (entity.user_name) {
    return `${entity.user_name}`;
  } else if (entity.first_name && entity.last_name) {
    return `${entity.first_name} ${entity.last_name}`;
  } else if (entity.first_name) {
    return `${entity.first_name}`;
  } else if (entity.last_name) {
    return `${entity.last_name}`;
  }

  return `${entity.external_id}`;
}
